import { useCallback, useEffect, useState } from 'react'
import { Space, Spin, Switch, Typography, message } from 'antd'
import { privacyApi, type PrivacySettings as Settings } from '../services/privacyApi'

const { Text } = Typography

function extractError(error: unknown): string {
    const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
    if (typeof detail === 'string') return detail
    return 'Не удалось выполнить действие'
}

export default function PrivacySettings() {
    const [settings, setSettings] = useState<Settings>({
        hide_lead_contacts: false,
        block_lead_edit: false,
        block_lead_delete: false,
        block_lead_blocking: false,
        hide_dashboard: false,
        hide_broadcast_queue: false,
        hide_lead_cards: false,
        hide_duplicates: false,
        hide_telegram_id: false,
    })
    const [loading, setLoading] = useState(true)

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const { data } = await privacyApi.getSettings()
            setSettings(data)
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        load()
    }, [load])

    const toggle = async (field: keyof Settings, checked: boolean) => {
        setSettings((current) => ({ ...current, [field]: checked }))
        try {
            await privacyApi.updateSettings({ [field]: checked })
        } catch (error) {
            setSettings((current) => ({ ...current, [field]: !checked }))
            message.error(extractError(error))
        }
    }

    return (
        <Spin spinning={loading}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Space size={12} align="start">
                    <Switch checked={settings.hide_lead_contacts} onChange={(checked) => toggle('hide_lead_contacts', checked)} />
                    <div>
                        <div>Скрывать контакты лида от операторов</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Телеграм, телефон, ссылку на чат и дополнительные контакты оператор не увидит
                            ни в карточке, ни в чате: сервер их не отдаёт вовсе, поэтому они не видны и в
                            коде страницы. Вместо них показывается ID CRM WW, по нему же работает поиск.
                            «Админ ЛПЕ» и «Админ разработки» видят всё как раньше.
                        </Text>
                    </div>
                </Space>

                <Space size={12} align="start">
                    <Switch checked={settings.hide_telegram_id} onChange={(checked) => toggle('hide_telegram_id', checked)} />
                    <div>
                        <div>Скрывать TG ID лида от операторов</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Отдельно от остальных контактов: оператор не увидит TG ID ни в карточке, ни в
                            истории изменений, и не сможет его поменять. Сервер значение не отдаёт.
                            «Админ ЛПЕ» и «Админ разработки» видят всё как раньше.
                        </Text>
                    </div>
                </Space>

                <Text type="secondary" style={{ fontSize: 12 }}>
                    Оператор по-прежнему ведёт переписку сам, поэтому имя и аватар собеседника, а также
                    контакты, которые лид напишет прямо в диалоге, скрыть невозможно.
                </Text>

                <Space size={12} align="start">
                    <Switch
                        checked={settings.block_lead_edit}
                        onChange={(checked) => toggle('block_lead_edit', checked)}
                    />
                    <div>
                        <div>Запретить операторам редактировать лида</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Кнопка «Редактировать» пропадает и в разделе «Карточки лидов», и в карточке,
                            открытой из чата, а сервер отклоняет изменение, даже если запрос отправить в
                            обход интерфейса. Выключено — оператор правит карточку в обоих местах.
                            Заметки, взятие в работу и переписка не затрагиваются.
                        </Text>
                    </div>
                </Space>

                <Space size={12} align="start">
                    <Switch
                        checked={settings.block_lead_blocking}
                        onChange={(checked) => toggle('block_lead_blocking', checked)}
                    />
                    <div>
                        <div>Запретить операторам блокировку лида</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Кнопка блокировки в шапке диалога пропадает, а сервер отклоняет запрос.
                            Блокировка выполняется рабочим TG-аккаунтом и снимается только вручную,
                            поэтому её имеет смысл оставить за «Админом ЛПЕ».
                        </Text>
                    </div>
                </Space>

                <Space size={12} align="start">
                    <Switch
                        checked={settings.block_lead_delete}
                        onChange={(checked) => toggle('block_lead_delete', checked)}
                    />
                    <div>
                        <div>Запретить операторам удалять лидов</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Кнопка удаления в карточке и в чате пропадает. Сам эндпоинт удаления и так
                            доступен только ролям «Админ ЛПЕ» и «Админ разработки» — переключатель убирает кнопку, которая
                            всё равно возвращала оператору отказ.
                        </Text>
                    </div>
                </Space>

                <Space size={12} align="start">
                    <Switch
                        checked={settings.hide_lead_cards}
                        onChange={(checked) => toggle('hide_lead_cards', checked)}
                    />
                    <div>
                        <div>Скрывать карточки лидов от операторов</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Раздел «Карточки лидов» пропадает из меню и не открывается по прямой ссылке,
                            а общий список лидов сервер оператору не отдаёт. Свои диалоги и карточка лида
                            внутри чата остаются доступны — без них оператор не сможет работать.
                        </Text>
                    </div>
                </Space>

                <Space size={12} align="start">
                    <Switch
                        checked={settings.hide_duplicates}
                        onChange={(checked) => toggle('hide_duplicates', checked)}
                    />
                    <div>
                        <div>Скрывать дубли от операторов</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Раздел «Дубли» пропадает из меню и не открывается по прямой ссылке,
                            а его данные сервер оператору не отдаёт.
                        </Text>
                    </div>
                </Space>

                <Space size={12} align="start">
                    <Switch
                        checked={settings.hide_dashboard}
                        onChange={(checked) => toggle('hide_dashboard', checked)}
                    />
                    <div>
                        <div>Скрывать дашборд от операторов</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Раздел «Дашборды» пропадает из меню и не открывается по прямой ссылке,
                            а его данные сервер оператору не отдаёт.
                        </Text>
                    </div>
                </Space>

                <Space size={12} align="start">
                    <Switch
                        checked={settings.hide_broadcast_queue}
                        onChange={(checked) => toggle('hide_broadcast_queue', checked)}
                    />
                    <div>
                        <div>Скрывать очередь от операторов</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Раздел «Очередь» пропадает из меню и не открывается по прямой ссылке,
                            а его данные сервер оператору не отдаёт.
                        </Text>
                    </div>
                </Space>
            </Space>
        </Spin>
    )
}
